/**
 * Pure parsers for Veo-style motion prompt text → structured segments.
 *
 * Extracted from `VideoGeneratorNode.tsx` (RFC: VideoGeneratorNode
 * decomposition). No React, no DOM — safe to call from any context.
 *
 * The wire format these parsers consume:
 *
 *     <clip header text>
 *     [0s-2.5s] Action description.  Camera: Wide pan.
 *     <dialogue>
 *     [0.5s-1.4s] [@Kai,cold]: "Stop right there."
 *     <voiceover>
 *     [3.0s-4.0s] [@narrator]: "Three years later…"
 *
 * Action segments live in the leading (unwrapped) block; dialogue +
 * voiceover segments live inside `<dialogue>` / `<voiceover>` blocks
 * which may appear in any order.
 */

export interface MotionSegment {
  type: 'action' | 'dialogue' | 'voiceover'
  timestamp: string
  startTime: number
  endTime: number
  action: string
  camera: string | null
  character?: string
  emotion?: string
}

export interface ParsedMotionPrompt {
  clipHeader: string | null
  segments: MotionSegment[]
  fallbackText: string | null
}

/** Extract numeric start time from a timestamp like "1.0s-3.5s" */
export function parseStartTime(ts: string): number {
  const match = ts.match(/^(\d+(?:\.\d+)?)s?/)
  return match ? parseFloat(match[1]) : 0
}

function parseTimestampRange(ts: string): { start: number; end: number } {
  const match = ts.match(/^(\d+(?:\.\d+)?)s?-(\d+(?:\.\d+)?)s?$/)
  if (!match) return { start: parseStartTime(ts), end: parseStartTime(ts) }
  return { start: parseFloat(match[1]), end: parseFloat(match[2]) }
}

/** Parse timestamped segments from a text block */
export function parseTimestampedSegments(
  block: string,
  segType: 'action' | 'dialogue' | 'voiceover',
): MotionSegment[] {
  const timestampRegex = /\[(\d+(?:\.\d+)?s?-\d+(?:\.\d+)?s)\]:?\s*/g
  const matches = [...block.matchAll(timestampRegex)]
  if (matches.length === 0) return []

  return matches.map((match, i) => {
    const start = match.index! + match[0].length
    const end = i < matches.length - 1 ? matches[i + 1].index! : block.length
    let segmentText = block.slice(start, end).trim()

    let camera: string | null = null
    let character: string | undefined
    let emotion: string | undefined

    if (segType === 'action') {
      const cameraMatch = segmentText.match(/Camera:\s*(.+?)$/i)
      if (cameraMatch) {
        camera = cameraMatch[1].trim()
        segmentText = segmentText.slice(0, cameraMatch.index).trim()
      }
    } else if (segType === 'dialogue' || segType === 'voiceover') {
      // New format: [@Character,emotion]:"text"
      const speakerMatch = segmentText.match(/^\[@([^\]]*)\]:?/)
      if (speakerMatch) {
        const speakerParts = speakerMatch[1] // e.g. "Kai,cold" or "narrator"
        const commaIdx = speakerParts.indexOf(',')
        if (commaIdx >= 0) {
          character = speakerParts.slice(0, commaIdx).trim()
          emotion = speakerParts.slice(commaIdx + 1).trim()
        } else {
          character = speakerParts.trim()
        }
        segmentText = segmentText.slice(speakerMatch[0].length).trim()
      } else {
        // Backward compat: old format "Character: \"text\""
        const colonIdx = segmentText.indexOf(':')
        if (colonIdx > 0) {
          character = segmentText.slice(0, colonIdx).trim()
          segmentText = segmentText.slice(colonIdx + 1).trim()
        }
      }
      // Strip surrounding ASCII/smart quotes regardless of speaker form, so
      // bare `[Ns-Ns]: ""` lines parse to empty text instead of literal `""`.
      segmentText = segmentText.replace(/^[""“]+|[""”]+$/g, '').trim()
    }

    const range = parseTimestampRange(match[1])
    return {
      type: segType,
      timestamp: match[1],
      startTime: range.start,
      endTime: range.end,
      action: segmentText,
      camera,
      character,
      emotion,
    }
  })
}

export function parseMotionPrompt(text: string): ParsedMotionPrompt {
  if (!text.trim()) return { clipHeader: null, segments: [], fallbackText: null }

  // Extract tagged blocks (<dialogue>, <voiceover>) and the leading action block.
  // Tags can appear in any order; everything before the first tag is the action block.
  const tagRegex = /<(dialogue|voiceover)>\n?/g
  const tagMatches = [...text.matchAll(tagRegex)]

  const actionBlock = tagMatches.length > 0 ? text.slice(0, tagMatches[0].index) : text

  const taggedBlocks: Array<{ type: 'dialogue' | 'voiceover'; content: string }> = []
  for (let i = 0; i < tagMatches.length; i++) {
    const start = tagMatches[i].index! + tagMatches[i][0].length
    const end = i < tagMatches.length - 1 ? tagMatches[i + 1].index! : text.length
    const blockType = tagMatches[i][1] === 'dialogue' ? 'dialogue' : 'voiceover'
    taggedBlocks.push({
      type: blockType,
      content: text.slice(start, end),
    })
  }

  const actionSegments = parseTimestampedSegments(actionBlock, 'action')
  const allTaggedSegments = taggedBlocks.flatMap(b => parseTimestampedSegments(b.content, b.type))

  if (actionSegments.length === 0 && allTaggedSegments.length === 0) {
    return { clipHeader: null, segments: [], fallbackText: text }
  }

  const firstTsMatch = actionBlock.match(/\[\d+(?:\.\d+)?s?-\d+(?:\.\d+)?s\]/)
  const clipHeader = firstTsMatch
    ? actionBlock.slice(0, firstTsMatch.index).replace(/:\s*$/, '').trim() || null
    : null

  const segments = [...actionSegments, ...allTaggedSegments].sort(
    (a, b) => a.startTime - b.startTime,
  )

  return { clipHeader, segments, fallbackText: null }
}
